// 7-segment LCD-style digit renderer.
// Draws lit + ghost (off) segments so the unlit shapes stay faintly visible,
// matching the look of a real LED stopwatch.

type Segments = {
  a?: boolean;
  b?: boolean;
  c?: boolean;
  d?: boolean;
  e?: boolean;
  f?: boolean;
  g?: boolean;
};

const DIGIT_SEGMENTS: Record<string, Segments> = {
  "0": { a: true, b: true, c: true, d: true, e: true, f: true },
  "1": { b: true, c: true },
  "2": { a: true, b: true, d: true, e: true, g: true },
  "3": { a: true, b: true, c: true, d: true, g: true },
  "4": { b: true, c: true, f: true, g: true },
  "5": { a: true, c: true, d: true, f: true, g: true },
  "6": { a: true, c: true, d: true, e: true, f: true, g: true },
  "7": { a: true, b: true, c: true },
  "8": { a: true, b: true, c: true, d: true, e: true, f: true, g: true },
  "9": { a: true, b: true, c: true, d: true, f: true, g: true },
};

type Ctx = OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;

const horizontalBar = (
  ctx: Ctx,
  cx: number,
  cy: number,
  length: number,
  thickness: number,
) => {
  const halfL = length / 2;
  const halfT = thickness / 2;
  ctx.beginPath();
  ctx.moveTo(cx - halfL, cy);
  ctx.lineTo(cx - halfL + halfT, cy - halfT);
  ctx.lineTo(cx + halfL - halfT, cy - halfT);
  ctx.lineTo(cx + halfL, cy);
  ctx.lineTo(cx + halfL - halfT, cy + halfT);
  ctx.lineTo(cx - halfL + halfT, cy + halfT);
  ctx.closePath();
  ctx.fill();
};

const verticalBar = (
  ctx: Ctx,
  cx: number,
  cy: number,
  length: number,
  thickness: number,
) => {
  const halfL = length / 2;
  const halfT = thickness / 2;
  ctx.beginPath();
  ctx.moveTo(cx, cy - halfL);
  ctx.lineTo(cx + halfT, cy - halfL + halfT);
  ctx.lineTo(cx + halfT, cy + halfL - halfT);
  ctx.lineTo(cx, cy + halfL);
  ctx.lineTo(cx - halfT, cy + halfL - halfT);
  ctx.lineTo(cx - halfT, cy - halfL + halfT);
  ctx.closePath();
  ctx.fill();
};

export const drawSevenSegmentDigit = (
  ctx: Ctx,
  x: number,
  y: number,
  w: number,
  h: number,
  digit: string,
  litColor: string,
  offColor: string,
) => {
  const segs = DIGIT_SEGMENTS[digit] ?? {};
  const t = w * 0.24;
  const padX = w * 0.05;
  const padY = h * 0.04;

  const cxMid = x + w / 2;
  const cxLeft = x + padX + t / 2;
  const cxRight = x + w - padX - t / 2;

  const cyTop = y + padY + t / 2;
  const cyMidH = y + h / 2;
  const cyBot = y + h - padY - t / 2;
  const cyUpper = (cyTop + cyMidH) / 2;
  const cyLower = (cyMidH + cyBot) / 2;

  const horizLen = w - padX * 2 - t;
  // Length tuned so the hexagon points exactly meet the horizontal bars'
  // diagonal tips — gives the classic interlocking-segment look.
  const vertLen = cyMidH - cyTop;

  const stroke = (lit: boolean | undefined, fn: () => void) => {
    ctx.fillStyle = lit ? litColor : offColor;
    fn();
  };

  stroke(segs.a, () => horizontalBar(ctx, cxMid, cyTop, horizLen, t));
  stroke(segs.b, () => verticalBar(ctx, cxRight, cyUpper, vertLen, t));
  stroke(segs.c, () => verticalBar(ctx, cxRight, cyLower, vertLen, t));
  stroke(segs.d, () => horizontalBar(ctx, cxMid, cyBot, horizLen, t));
  stroke(segs.e, () => verticalBar(ctx, cxLeft, cyLower, vertLen, t));
  stroke(segs.f, () => verticalBar(ctx, cxLeft, cyUpper, vertLen, t));
  stroke(segs.g, () => horizontalBar(ctx, cxMid, cyMidH, horizLen, t));
};

export const drawColon = (
  ctx: Ctx,
  x: number,
  y: number,
  w: number,
  h: number,
  color: string,
) => {
  ctx.fillStyle = color;
  const dotW = w * 0.78;
  const cx = x + w / 2;
  const cyUpper = y + h * 0.34;
  const cyLower = y + h * 0.66;
  const r = dotW * 0.18;
  ctx.beginPath();
  ctx.roundRect(cx - dotW / 2, cyUpper - dotW / 2, dotW, dotW, r);
  ctx.fill();
  ctx.beginPath();
  ctx.roundRect(cx - dotW / 2, cyLower - dotW / 2, dotW, dotW, r);
  ctx.fill();
};
